import { useState, useEffect } from "react";

import {
  TextField,
  MenuItem,
  Grid,
  Stack,
  Switch,
  Typography,
  Autocomplete,
  Divider,
} from "@pankod/refine-mui";
import { Controller } from "@pankod/refine-react-hook-form";
import MultiSelect from "components/MultiSelect";
import MultiSelectCheckbox from "components/MultiSelect-Checkbox";
import UploadZone from "components/UploadZone";
import GalleryManager from "components/GalleryManager";
import CustomDatePicker from "components/DatePickerMUI";
import useLocales from "locales/useLocales";

export default function CompFields({
  fieldVariants,
  label,
  name,
  variant,
  required,
  defaultValue,
  defaultLanguageRecord,
  type,
  inputProps = {},
  options,
  accessor,
  lines,
  xs,
  md,
  height,
  disabled,
  register,
  control,
  errors,
  setError,
  getValues,
  setValue,
  watch,
  localized = false,
  disablePast,
  disableDay,
  validation,
}) {
  const { currentLang, defaultLang } = useLocales();

  const [autocompleteValue, setAutocompleteValue] = useState(null);
  const [isLocalized, setIsLocalized] = useState(true); //true if this content type is localized

  useEffect(() => {
    if (
      (type == "autocomplete" ||
        type == "multi-select" ||
        type == "multi-select-checkbox") &&
      defaultValue &&
      autocompleteValue == null
    ) {
      setValue(name, defaultValue, { shouldValidate: true });
      setAutocompleteValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    let localizations = defaultLanguageRecord?.localizations;
    if (defaultLanguageRecord && !localizations) setIsLocalized(false);

    if (defaultLanguageRecord && currentLang.value != defaultLang) {
      //console.log("Setting", defaultLanguageRecord[name]);
      if (!localized && name) {
        /*
        When I have the property path as a string, 
        I need to split the string into an array of keys and then use a loop to access the nested properties. 
        */
        let keys = name.split(".");
        let currentLevel = defaultLanguageRecord;
        // Loop through the keys and access the nested properties
        for (let key of keys) {
          if (currentLevel && currentLevel.hasOwnProperty(key)) {
            currentLevel = currentLevel[key];
          } else {
            // Handle the case where the key doesn't exist
            console.error(`Property "${key}" not found`);
            break;
          }
        }
        setValue(name, currentLevel, { shouldValidate: true });
      }
    }
  }, [currentLang.value, defaultLanguageRecord]);

  const defaultValueData =
    defaultLanguageRecord && currentLang.value != defaultLang
      ? defaultLanguageRecord[name]
      : defaultValue || watch(name);

  /*disabled =
    disabled ||
    (!localized &&
      defaultLanguageRecord &&
      currentLang.value != defaultLang &&
      isLocalized);*/

  if (type == "title")
    return (
      <Grid item xs={12} sx={{ height: height || "100%" }}>
        <Typography variant={variant} gutterBottom component="div">
          {label}
        </Typography>
      </Grid>
    );
  if (type == "fixed_text")
    return (
      <Grid
        item
        xs={xs || 12}
        md={md || 12}
        container
        justifyContent="flex-start"
        flexDirection="column"
        sx={{ height: height || "100%" }}
        gap={1}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }} gutterBottom>
          {label}:
        </Typography>
        <Typography variant="body2" gutterBottom>
          {defaultValue}
        </Typography>
      </Grid>
    );
  if (type == "space")
    return <Grid item xs={12} sx={{ height: height || "100%" }}></Grid>;
  if (type == "divider")
    return (
      <Grid
        item
        xs={12}
        container
        justifyContent="center"
        sx={{ height: height || "100%" }}
      >
        <Grid item xs={xs || 12}>
          <Divider sx={{ my: 2 }} />
        </Grid>
      </Grid>
    );

  if (type == "text")
    return (
      <Grid item xs={xs || 12} md={md || 12} sx={{ height: height || "100%" }}>
        <TextField
          {...register(name, {
            required: required ? label + " Obbligatorio" : false,
            validate: validation,
          })}
          InputLabelProps={{
            shrink: true,
          }}
          error={!!errors[name]}
          helperText={errors[name]?.message}
          required={required}
          variant={fieldVariants || "standard"}
          defaultValue={defaultValue != null ? defaultValue : defaultValueData}
          fullWidth
          label={label}
          type={type}
          inputProps={{
            ...inputProps,
            "data-localized": localized,
          }} /* data-* is a custom attribute, and you can set its value to whatever information you need.
          To access this data using JavaScript, you can use the dataset property:
          var customOptionValue = inputElement.dataset.*;
          or the method getAttribute()
          I will use this attribute to determine if the textfield is localized*/
          multiline={lines && lines > 1}
          rows={lines}
          disabled={disabled}
        />
      </Grid>
    );

  if (
    type == "number" ||
    //type == "date" ||
    type == "time" ||
    type == "datetime"
  )
    return (
      <Grid item xs={xs || 12} md={md || 12} sx={{ height: height || "100%" }}>
        <Controller
          disabled={disabled}
          control={control}
          name={name}
          rules={{
            required: required ? label + " Obbligatorio" : false,
            validate: validation,
          }}
          defaultValue={defaultValue != null ? defaultValue : defaultValueData}
          render={({ field }) => {
            return (
              <TextField
                value={field.value}
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!errors[name]}
                helperText={errors[name]?.message}
                required={required}
                variant={fieldVariants || "standard"}
                fullWidth
                label={label}
                type={type == "datetime" ? "datetime-local" : type}
                inputProps={{
                  ...inputProps,
                  "data-localized": localized,
                }} /* data-* is a custom attribute, and you can set its value to whatever information you need.
          To access this data using JavaScript, you can use the dataset property:
          var customOptionValue = inputElement.dataset.*;
          or the method getAttribute()
          I will use this attribute to determine if the textfield is localized*/
                onChange={
                  type == "time"
                    ? (e) => {
                        //console.log(`${e.target.value}:00.000`);
                        field.onChange(`${e.target.value}:00.000`);
                        setValue(name, `${e.target.value}:00.000`);
                      }
                    : (e) => {
                        field.onChange(e.target.value);
                        setValue(name, e.target.value, {
                          shouldValidate: true,
                        });
                      }
                }
                multiline={lines && lines > 1}
                rows={lines}
                disabled={disabled}
              />
            );
          }}
        />
      </Grid>
    );
  if (type == "date")
    return (
      <Grid item xs={xs || 12} md={md || 12} sx={{ height: height || "100%" }}>
        <Controller
          disabled={disabled}
          control={control}
          name={name}
          rules={{
            required: required ? label + " Obbligatorio" : false,
            validate: validation,
          }}
          defaultValue={defaultValue != null ? defaultValue : defaultValueData}
          render={({ field }) => {
            return (
              <CustomDatePicker
                /*{...register(name, {
                  required: required ? label + " Obbligatorio" : false,
                })}*/
                error={!!errors[name]}
                helperText={errors[name]?.message}
                required={required}
                variant={fieldVariants || "standard"}
                /*defaultValue={
                  defaultValue != null ? defaultValue : defaultValueData
                }*/
                fullWidth={true}
                label={label}
                disabled={disabled}
                value={field.value != null ? field.value : ""}
                onChange={(e) => {
                  field.onChange(e);
                  setValue(name, e, {
                    shouldValidate: true,
                  });
                }}
                //value={getValues(name) != null ? getValues(name) : ""}
                //onChange={(e) => setValue(name, e, { shouldValidate: true })}
                disableDay={disableDay}
                disablePast={disablePast}
              />
            );
          }}
        />
      </Grid>
    );
  if (type == "switch")
    return (
      <Grid item xs={xs || 12} md={md || 12} sx={{ height: height || "100%" }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle1" color="text.secondary">
            {label}
          </Typography>
          <Switch
            {...register(name, {})}
            control={control}
            checked={defaultValue || defaultValueData}
            disabled={disabled}
          />
        </Stack>
      </Grid>
    );
  if (type == "select")
    return (
      <Grid item xs={xs || 12} md={md || 12} sx={{ height: height || "100%" }}>
        <TextField
          {...register(name, {
            required: required ? label + " Obbligatorio" : false,
          })}
          InputLabelProps={{
            shrink: true,
          }}
          error={!!errors[name]}
          helperText={errors[name]?.message}
          required={required}
          variant={fieldVariants || "standard"}
          defaultValue={defaultValue}
          fullWidth
          label={label}
          select
          value={getValues(name) != null ? getValues(name) : ""}
          onChange={(e) => {
            //console.log(e.target.value);
            setValue(name, e.target.value, { shouldValidate: true });
          }}
          disabled={disabled}
        >
          {options.map((option, index) => (
            <MenuItem key={index} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    );
  if (type == "autocomplete")
    return (
      <Grid item xs={xs || 12} md={md || 12} sx={{ height: height || "100%" }}>
        <Controller
          disabled={disabled}
          control={control}
          name={name}
          rules={{ required: required ? label + " Obbligatorio" : false }}
          render={({ field }) => {
            /* sostituisco field.value con la versione popolata con i localizations
            in modo da visualizzare l'opzione selezionata nella lingua corretta*/
            let populatedValue = undefined;
            if (options.options.length > 0) {
              //console.log(options);
              let id = field.value?.id;
              //console.log(id);
              let idx = options.options.map((item) => item.id).indexOf(id);
              if (idx >= 0) {
                //console.log(options.options[idx]);
                populatedValue = options.options[idx];
              }
            }
            return (
              <Autocomplete
                {...options}
                {...field}
                value={
                  populatedValue
                    ? populatedValue
                    : field.value || autocompleteValue
                }
                onChange={(_, value) => {
                  field.onChange(value);
                }}
                getOptionLabel={(item) => {
                  //ho spostato questa traduzione a monte, direttamente nel dataProvider
                  /*show the value in the proper language, if exist
                  if (currentLang.value != defaultLang) {
                    let localizations = item?.localizations;
                    if (localizations) {
                      let idx = localizations
                        .map((locale) => locale.locale)
                        .indexOf(currentLang.value);
                      if (idx >= 0)
                        return localizations[idx][accessor]
                          ? localizations[idx][accessor]
                          : "";
                    }
                  }*/
                  return item[accessor] ? item[accessor] : "";
                }}
                isOptionEqualToValue={(option, value) => {
                  return value === undefined || option.id === value.id;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={label}
                    variant={fieldVariants || "outlined"}
                    error={!!errors[name]}
                    helperText={errors[name]?.message}
                    required
                  />
                )}
              />
            );
          }}
        />
      </Grid>
    );
  if (type == "multi-select")
    return (
      <Grid item xs={xs || 12} md={md || 12} sx={{ height: height || "100%" }}>
        <Controller
          disabled={disabled}
          control={control}
          name={name}
          render={({ field }) => {
            return (
              <MultiSelect
                disabled={disabled}
                name={label}
                variant={fieldVariants || "standard"}
                options={options}
                value={field.value || autocompleteValue}
                defaultValue={defaultValue}
                onChange={(val) => {
                  field.onChange(val);
                  setValue(name, val, { shouldValidate: true });
                }}
              />
            );
          }}
        />
      </Grid>
    );
  if (type == "multi-select-checkbox")
    return (
      <Grid item xs={xs || 12} md={md || 12} sx={{ height: height || "100%" }}>
        <Controller
          control={control}
          name={name}
          render={({ field }) => {
            return (
              <MultiSelectCheckbox
                disabled={disabled}
                name={label}
                variant={fieldVariants || "standard"}
                options={options}
                value={field.value || autocompleteValue}
                //defaultValue={defaultValue}
                onChange={(val) => {
                  field.onChange(val);
                  setValue(name, val, { shouldValidate: true });
                }}
              />
            );
          }}
        />
      </Grid>
    );
  if (type == "image")
    return (
      <Grid
        item
        xs={xs || 12}
        md={md || 12}
        sx={{
          height: height || "100%",
        }}
      >
        <UploadZone
          disabled={disabled}
          register={register}
          errors={errors}
          setError={setError}
          setValue={setValue}
          defaultValue={defaultValue || defaultValueData}
          fieldName={name}
          required={required}
        />
      </Grid>
    );
  if (type == "gallery")
    return (
      <Grid
        item
        xs={xs || 12}
        md={md || 12}
        sx={{ height: height || "100%", mt: 2 }}
      >
        <Controller
          control={control}
          name={name}
          rules={{ required: required ? label + " Obbligatorio" : false }}
          render={({ field }) => (
            <GalleryManager
              fieldName={field.name}
              control={control}
              errors={errors}
              setError={setError}
              defaultValue={watch(field.name)}
              getValues={getValues}
              setValue={setValue}
              watch={watch}
              disabled={disabled}
            />
          )}
        />
      </Grid>
    );
}
